<template>
  <div class="membership-level-form">
    <!-- 页面头部 -->
    <div class="page-header">
      <div class="header-content">
        <div class="header-left">
          <el-button @click="$router.go(-1)" size="medium" class="back-btn">
            <i class="el-icon-arrow-left"></i> 返回
          </el-button>
          <div class="header-info">
            <h1 class="page-title">
              <i class="el-icon-medal"></i>
              {{ isEdit ? '编辑会员等级' : '新增会员等级' }}
            </h1>
            <p class="page-subtitle">{{ isEdit ? '修改会员等级信息和权益设置' : '创建新的会员等级，设置折扣率和升级条件' }}</p>
          </div>
        </div>
        <div class="header-actions">
          <el-button @click="handleCancel" size="medium">
            <i class="el-icon-close"></i> 取消
          </el-button>
          <el-button type="primary" @click="handleSubmit" :loading="submitLoading" size="medium">
            <i class="el-icon-check"></i> {{ isEdit ? '保存修改' : '创建等级' }}
          </el-button>
        </div>
      </div>
    </div>

    <!-- 表单内容 -->
    <div class="main-content">
      <div class="form-container">
        <el-form
          ref="membershipLevelForm"
          :model="form"
          :rules="rules"
          label-width="120px"
          class="membership-level-form-content"
        >
          <!-- 基础信息 -->
          <div class="form-section">
            <div class="section-header">
              <h3><i class="el-icon-info"></i>基础信息</h3>
              <p>设置会员等级的名称、代码和排序</p>
            </div>
            
            <div class="form-grid">
              <el-form-item label="等级名称" prop="levelName" class="form-item">
                <el-input
                  v-model="form.levelName"
                  placeholder="请输入等级名称，如：钻石会员"
                  maxlength="50"
                  show-word-limit
                  clearable
                />
                <div class="form-tip">等级名称将显示给用户，建议使用简洁明了的名称</div>
              </el-form-item>

              <el-form-item label="等级代码" prop="levelCode" class="form-item">
                <el-input
                  v-model="form.levelCode"
                  placeholder="请输入等级代码，如：DIAMOND"
                  maxlength="20"
                  show-word-limit
                  clearable
                  :disabled="isEdit"
                  style="text-transform: uppercase"
                />
                <div class="form-tip">等级代码用于系统内部识别，只能包含大写字母、数字和下划线，创建后不可修改</div>
              </el-form-item>

              <el-form-item label="排序序号" prop="sortOrder" class="form-item">
                <el-input-number
                  v-model="form.sortOrder"
                  :min="0"
                  :max="999"
                  controls-position="right"
                  style="width: 100%"
                />
                <div class="form-tip">数字越小排序越靠前，用于控制等级显示顺序</div>
              </el-form-item>

              <el-form-item label="等级状态" prop="isActive" class="form-item">
                <el-switch
                  v-model="form.isActive"
                  active-text="启用"
                  inactive-text="禁用"
                />
                <div class="form-tip">禁用后，用户无法升级到此等级</div>
              </el-form-item>
            </div>
          </div>

          <!-- 折扣设置 -->
          <div class="form-section">
            <div class="section-header">
              <h3><i class="el-icon-coin"></i>折扣设置</h3>
              <p>设置会员等级的专享折扣率</p>
            </div>
            
            <div class="form-grid">
              <el-form-item label="折扣率" prop="discountRate" class="form-item discount-item">
                <div class="discount-input-wrapper">
                  <el-slider
                    v-model="discountSlider"
                    :min="1"
                    :max="100"
                    :step="1"
                    show-input
                    input-size="medium"
                    @change="handleDiscountChange"
                  />
                  <div class="discount-display">
                    <div class="discount-percent">{{ discountSlider }}%</div>
                    <div class="discount-description">
                      {{ discountSlider === 100 ? '原价，无折扣' : `${(discountSlider / 10).toFixed(1)}折优惠` }}
                    </div>
                  </div>
                </div>
                <div class="form-tip">设置该等级用户享受的折扣率，100%表示原价</div>
              </el-form-item>
            </div>
          </div>

          <!-- 升级条件 -->
          <div class="form-section">
            <div class="section-header">
              <h3><i class="el-icon-trophy"></i>升级条件</h3>
              <p>设置用户升级到该等级所需满足的条件</p>
            </div>
            
            <div class="form-grid">
              <el-form-item label="最低消费金额" prop="minPurchaseAmount" class="form-item">
                <el-input-number
                  v-model="form.minPurchaseAmount"
                  :min="0"
                  :precision="2"
                  controls-position="right"
                  style="width: 100%"
                />
                <div class="form-tip">用户累计消费达到此金额才能升级到该等级</div>
              </el-form-item>

              <el-form-item label="最低购买数量" prop="minPurchaseQuantity" class="form-item">
                <el-input-number
                  v-model="form.minPurchaseQuantity"
                  :min="0"
                  controls-position="right"
                  style="width: 100%"
                />
                <div class="form-tip">用户累计购买达到此数量才能升级到该等级</div>
              </el-form-item>
            </div>
          </div>

          <!-- 等级权益 -->
          <div class="form-section">
            <div class="section-header">
              <h3><i class="el-icon-star-on"></i>等级权益</h3>
              <p>详细描述该等级用户可享受的专属权益</p>
            </div>
            
            <el-form-item label="权益说明" prop="levelBenefits" class="form-item benefits-item">
              <el-input
                v-model="form.levelBenefits"
                type="textarea"
                :rows="4"
                placeholder="请详细描述该等级用户可享受的专属权益，如：享受7折优惠、专属客服、优先发货等"
                maxlength="1000"
                show-word-limit
              />
              <div class="form-tip">详细的权益说明有助于用户了解等级价值，提升升级积极性</div>
            </el-form-item>
          </div>

          <!-- 预览区域 -->
          <div class="form-section">
            <div class="section-header">
              <h3><i class="el-icon-view"></i>等级预览</h3>
              <p>查看等级信息的最终显示效果</p>
            </div>
            
            <div class="level-preview">
              <div class="preview-card">
                <div class="preview-header">
                  <div class="level-badge">{{ form.levelName || '等级名称' }}</div>
                  <div class="level-code">{{ form.levelCode || 'LEVEL_CODE' }}</div>
                </div>
                <div class="preview-content">
                  <div class="discount-info">
                    <div class="discount-rate">{{ discountSlider }}%</div>
                    <div class="discount-text">
                      {{ discountSlider === 100 ? '原价' : `${(discountSlider / 10).toFixed(1)}折` }}
                    </div>
                  </div>
                  <div class="conditions">
                    <div class="condition-item">
                      <i class="el-icon-coin"></i>
                      <span>消费满 ¥{{ form.minPurchaseAmount || 0 }}</span>
                    </div>
                    <div class="condition-item">
                      <i class="el-icon-shopping-bag-1"></i>
                      <span>购买满 {{ form.minPurchaseQuantity || 0 }} 件</span>
                    </div>
                  </div>
                  <div class="benefits">
                    <div class="benefits-title">专属权益</div>
                    <div class="benefits-content">
                      {{ form.levelBenefits || '暂无权益说明' }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </el-form>
      </div>
    </div>
  </div>
</template>

<script>
import { 
  createMembershipLevel, 
  updateMembershipLevel, 
  getMembershipLevelDetail 
} from '@/api/membershipLevel'

export default {
  name: 'MembershipLevelForm',
  
  data() {
    return {
      submitLoading: false,
      levelId: null,
      isEdit: false,
      discountSlider: 100,
      form: {
        levelName: '',
        levelCode: '',
        discountRate: 1.00,
        minPurchaseAmount: 0,
        minPurchaseQuantity: 0,
        levelBenefits: '',
        sortOrder: 1,
        isActive: true
      },
      rules: {
        levelName: [
          { required: true, message: '请输入等级名称', trigger: 'blur' },
          { min: 1, max: 50, message: '等级名称长度为1-50个字符', trigger: 'blur' }
        ],
        levelCode: [
          { required: true, message: '请输入等级代码', trigger: 'blur' },
          { min: 1, max: 20, message: '等级代码长度为1-20个字符', trigger: 'blur' },
          { pattern: /^[A-Z0-9_]+$/, message: '等级代码只能包含大写字母、数字和下划线', trigger: 'blur' }
        ],
        discountRate: [
          { required: true, message: '请设置折扣率', trigger: 'blur' },
          { type: 'number', min: 0.01, max: 1.00, message: '折扣率范围为0.01-1.00', trigger: 'blur' }
        ],
        minPurchaseAmount: [
          { required: true, message: '请设置最低消费金额', trigger: 'blur' },
          { type: 'number', min: 0, message: '最低消费金额不能小于0', trigger: 'blur' }
        ],
        minPurchaseQuantity: [
          { required: true, message: '请设置最低购买数量', trigger: 'blur' },
          { type: 'number', min: 0, message: '最低购买数量不能小于0', trigger: 'blur' }
        ],
        sortOrder: [
          { required: true, message: '请设置排序序号', trigger: 'blur' },
          { type: 'number', min: 0, message: '排序序号不能小于0', trigger: 'blur' }
        ],
        levelBenefits: [
          { max: 1000, message: '权益说明最多1000个字符', trigger: 'blur' }
        ]
      }
    }
  },
  
  async mounted() {
    this.levelId = this.$route.params.id
    this.isEdit = !!this.levelId && this.$route.path.includes('edit')
    
    if (this.isEdit) {
      await this.loadMembershipLevel()
    }
  },
  
  methods: {
    // 加载会员等级详情
    async loadMembershipLevel() {
      try {
        const response = await getMembershipLevelDetail(this.levelId)
        const data = response.data
        
        this.form = {
          levelName: data.levelName,
          levelCode: data.levelCode,
          discountRate: data.discountRate,
          minPurchaseAmount: data.minPurchaseAmount,
          minPurchaseQuantity: data.minPurchaseQuantity,
          levelBenefits: data.levelBenefits || '',
          sortOrder: data.sortOrder,
          isActive: data.isActive
        }
        
        // 更新滑块值
        this.discountSlider = Math.round(data.discountRate * 100)
        
      } catch (error) {
        console.error('获取会员等级详情失败:', error)
        this.$message.error('获取会员等级详情失败')
        this.$router.back()
      }
    },
    
    // 折扣率变更
    handleDiscountChange(value) {
      this.form.discountRate = value / 100
    },
    
    // 提交表单
    async handleSubmit() {
      try {
        await this.$refs.membershipLevelForm.validate()
        
        this.submitLoading = true
        
        const formData = {
          ...this.form,
          levelCode: this.form.levelCode.toUpperCase()
        }
        
        if (this.isEdit) {
          await updateMembershipLevel(this.levelId, formData)
          this.$message.success('会员等级更新成功')
        } else {
          await createMembershipLevel(formData)
          this.$message.success('会员等级创建成功')
        }
        
        this.$router.push('/admin/membership-levels')
        
      } catch (error) {
        console.error('保存会员等级失败:', error)
        this.$message.error(error.message || '保存会员等级失败')
      } finally {
        this.submitLoading = false
      }
    },
    
    // 取消操作
    handleCancel() {
      this.$confirm('确定要取消操作吗？未保存的修改将丢失', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '继续编辑',
        type: 'warning'
      }).then(() => {
        this.$router.back()
      }).catch(() => {
        // 用户选择继续编辑
      })
    }
  },
  
  watch: {
    // 监听表单中的levelCode变化，自动转换为大写
    'form.levelCode': function(newVal) {
      if (newVal) {
        this.form.levelCode = newVal.toUpperCase()
      }
    }
  }
}
</script>

<style scoped>
/* 页面基础样式 */
.membership-level-form {
  min-height: 100vh;
  background: #f5f7fa;
}

/* 页面头部 */
.page-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 25px 40px;
  margin-bottom: 30px;
  box-shadow: 0 2px 10px rgba(102, 126, 234, 0.3);
}

.header-content {
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-left {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 20px;
}

.back-btn {
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: white;
}

.back-btn:hover {
  background: rgba(255, 255, 255, 0.3);
  border-color: rgba(255, 255, 255, 0.5);
}

.header-info .page-title {
  font-size: 28px;
  font-weight: 600;
  margin: 0 0 8px 0;
  display: flex;
  align-items: center;
  gap: 12px;
  color: white;
}

.header-info .page-title i {
  font-size: 32px;
  color: white;
}

.header-info .page-subtitle {
  font-size: 16px;
  opacity: 0.9;
  margin: 0;
  line-height: 1.5;
  color: white;
}

.header-actions {
  display: flex;
  gap: 12px;
}

.header-actions .el-button {
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: white;
  font-weight: 500;
}

.header-actions .el-button--primary {
  background: rgba(255, 255, 255, 0.95);
  color: #667eea;
  border-color: transparent;
}

.header-actions .el-button:hover {
  background: rgba(255, 255, 255, 0.3);
  border-color: rgba(255, 255, 255, 0.5);
}

.header-actions .el-button--primary:hover {
  background: white;
  color: #667eea;
}

/* 主内容区域 */
.main-content {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 30px;
}

.form-container {
  background: white;
  border-radius: 20px;
  padding: 40px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid #f1f3f4;
}

/* 表单区块 */
.form-section {
  margin-bottom: 40px;
}

.form-section:last-child {
  margin-bottom: 0;
}

.section-header {
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 2px solid #f0f2f5;
}

.section-header h3 {
  font-size: 20px;
  font-weight: 600;
  color: #303133;
  margin: 0 0 8px 0;
  display: flex;
  align-items: center;
  gap: 10px;
}

.section-header h3 i {
  color: #667eea;
  font-size: 22px;
}

.section-header p {
  font-size: 14px;
  color: #666;
  margin: 0;
  line-height: 1.5;
}

/* 表单网格 */
.form-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;
}

.form-item {
  margin-bottom: 0;
}

.form-item.discount-item,
.form-item.benefits-item {
  grid-column: 1 / -1;
}

.form-tip {
  font-size: 12px;
  color: #909399;
  margin-top: 4px;
  line-height: 1.4;
}

/* 折扣率输入 */
.discount-input-wrapper {
  display: flex;
  align-items: center;
  gap: 20px;
}

.discount-display {
  text-align: center;
  min-width: 100px;
}

.discount-percent {
  font-size: 24px;
  font-weight: 600;
  color: #e6a23c;
}

.discount-description {
  font-size: 12px;
  color: #909399;
  margin-top: 4px;
}

/* 等级预览 */
.level-preview {
  margin-top: 20px;
}

.preview-card {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 16px;
  padding: 24px;
  color: white;
  max-width: 400px;
  margin: 0 auto;
}

.preview-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.level-badge {
  font-size: 20px;
  font-weight: 600;
}

.level-code {
  font-size: 12px;
  background: rgba(255, 255, 255, 0.2);
  padding: 4px 8px;
  border-radius: 4px;
  font-family: 'Courier New', monospace;
}

.preview-content {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.discount-info {
  text-align: center;
  padding: 16px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 8px;
}

.discount-info .discount-rate {
  font-size: 32px;
  font-weight: 700;
}

.discount-info .discount-text {
  font-size: 14px;
  opacity: 0.9;
}

.conditions {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.condition-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
}

.condition-item i {
  font-size: 16px;
}

.benefits {
  background: rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 16px;
}

.benefits-title {
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 8px;
}

.benefits-content {
  font-size: 13px;
  line-height: 1.5;
  opacity: 0.9;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .page-header {
    padding: 20px;
  }
  
  .header-content {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }
  
  .header-actions {
    width: 100%;
  }
  
  .header-actions .el-button {
    flex: 1;
  }
  
  .main-content {
    padding: 16px 20px;
  }
  
  .form-container {
    padding: 24px 20px;
  }
  
  .form-grid {
    grid-template-columns: 1fr;
    gap: 16px;
  }
  
  .discount-input-wrapper {
    flex-direction: column;
    gap: 12px;
  }
  
  .preview-card {
    max-width: none;
  }
}
</style>
